import { useCallback, useRef } from "react";

import { useQueryClient } from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";

import { supabase } from "@/shared/lib/supabase";
import { deleteDocument } from "@/shared/utils/deleteDocument";
import { sentryUtils } from "@/shared/utils/sentry";

interface QuickIdCheckCallbacks {
  onVerified: () => void;
  onFailed: () => void;
  onSkipped: () => void;
  onRejected: () => void;
}

export function useQuickIdCheck(estateId: number | undefined) {
  const queryClient = useQueryClient();
  const hasRun = useRef(false);

  const runCheck = useCallback(
    async (documentId: number, callbacks: QuickIdCheckCallbacks) => {
      if (hasRun.current || !estateId) return;
      hasRun.current = true;

      const { data: flagData } = await supabase
        .from("feature_flags")
        .select("enabled")
        .eq("name", "id_verification_enabled")
        .single();

      if (!(flagData?.enabled ?? true)) {
        queryClient.invalidateQueries({ queryKey: ["idVerificationStatus"] });
        callbacks.onSkipped();
        return;
      }

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!session) throw new Error("Not authenticated");

        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/documents/${estateId}/document/${documentId}/quick-id-check`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${session.access_token}`,
              apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
            },
          },
        );

        if (!res.ok) throw new Error(`ID check failed: ${res.status}`);

        const result: { isId: boolean } = await res.json();

        if (!result.isId) {
          await deleteDocument(estateId, documentId);
          queryClient.invalidateQueries({ queryKey: ["existingIdDocument", estateId] });
          hasRun.current = false;
          enqueueSnackbar(
            "That doesn't look like an ID. Please upload a government-issued photo ID.",
            { variant: "error" },
          );
          callbacks.onRejected();
          return;
        }

        queryClient.invalidateQueries({ queryKey: ["idVerificationStatus"] });
        callbacks.onVerified();
      } catch (err) {
        sentryUtils.captureError(err, "Quick ID check failed", { estateId, documentId });
        hasRun.current = false;
        enqueueSnackbar("ID verification failed. Please try again.", { variant: "error" });
        callbacks.onFailed();
      }
    },
    [estateId, queryClient],
  );

  const reset = useCallback(() => {
    hasRun.current = false;
  }, []);

  return { runCheck, reset };
}
